import { z } from 'zod';
/* ==========================================================================
 *  Saved meals wire contract (T11.1 / ISSUE-070)
 *
 *  saved_meal: a named bundle of food portions for 1-tap re-logging.
 *  Applying a saved meal creates INDEPENDENT copies in meal_entry — edits to
 *  the saved_meal never mutate past logs (immutability like targets).
 *  kind='recipe' is reserved for T11.4 (recipe builder).
 * ========================================================================== */
export const savedMealKindSchema = z.enum(['meal', 'recipe']);
export const savedMealItemSchema = z.object({
    id: z.string().uuid(),
    savedMealId: z.string().uuid(),
    foodId: z.string().nullable(),
    name: z.string(),
    qty: z.number().positive().nullable(),
    unit: z.string().nullable(),
    kcal: z.number().nonnegative(),
    proteinG: z.number().nonnegative(),
    carbsG: z.number().nonnegative(),
    fatG: z.number().nonnegative(),
    fiberG: z.number().nonnegative(),
});
export const savedMealSchema = z.object({
    id: z.string().uuid(),
    userId: z.string(),
    name: z.string(),
    kind: savedMealKindSchema,
    servings: z.number().int().positive(),
    isFavorite: z.boolean(),
    lastUsedAt: z.string().datetime().nullable(),
    items: z.array(savedMealItemSchema),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    /** Set when this meal was created via "save as variant" from a #ref delta
     *  log (T30.1). Null for ordinary saved meals. */
    parentId: z.string().uuid().nullable().default(null),
});
/* ----- Request schemas ---------------------------------------------------- */
/** A single item provided when creating a saved meal directly (not from entries). */
export const savedMealItemInputSchema = z.object({
    foodId: z.string().nullable().optional(),
    name: z.string().min(1),
    qty: z.number().positive().optional(),
    unit: z.string().optional(),
    kcal: z.number().nonnegative(),
    proteinG: z.number().nonnegative(),
    carbsG: z.number().nonnegative(),
    fatG: z.number().nonnegative(),
    fiberG: z.number().nonnegative(),
});
/**
 * POST /me/saved-meals
 * Either source existing entries by ID OR supply item macros directly.
 * Use kind='recipe' + servings to build a recipe (T11.4).
 */
export const createSavedMealSchema = z
    .object({
    name: z.string().min(1).max(120),
    /** 'meal' (default) or 'recipe'. Recipes use per-serving math when logged. */
    kind: savedMealKindSchema.default('meal'),
    /**
     * Number of servings this recipe yields. Only meaningful when kind='recipe'.
     * Logging logs total_macros/servings per item (one serving).
     */
    servings: z.number().int().positive().default(1),
    /** IDs of existing meal_entry rows to snapshot into the saved meal. */
    fromEntryIds: z.array(z.string().uuid()).optional(),
    /** Explicit item list (for direct-macro or future use). */
    items: z.array(savedMealItemInputSchema).optional(),
})
    .refine((v) => (v.fromEntryIds && v.fromEntryIds.length > 0) || (v.items && v.items.length > 0), {
    message: 'Provide at least one of fromEntryIds or items.',
});
/** PATCH /me/saved-meals/:id — rename, toggle favourite, or update servings */
export const updateSavedMealSchema = z.object({
    name: z.string().min(1).max(120).optional(),
    isFavorite: z.boolean().optional(),
    /** Update serving count for recipes. */
    servings: z.number().int().positive().optional(),
});
/** GET /me/saved-meals query params */
export const listSavedMealsQuerySchema = z.object({
    favorite: z
        .string()
        .transform((v) => v === 'true')
        .optional(),
    sort: z.enum(['recent', 'name']).optional().default('recent'),
});
/** POST /me/saved-meals/:id/log — apply a saved meal to a day */
export const logSavedMealSchema = z.object({
    /** Target local date (YYYY-MM-DD). Defaults to today when omitted. */
    date: z.string().date().optional(),
    /** Optional wall-clock time hint (ISO-8601 or HH:MM). Defaults to now. */
    time: z.string().optional(),
    /** User's IANA timezone (for correct day bucketing). */
    timezone: z.string().optional(),
});
/** Response for POST /me/saved-meals/:id/log */
export const logSavedMealResponseSchema = z.object({
    rawLogId: z.string().uuid(),
    entryCount: z.number().int().nonnegative(),
});
export const savedMealsListSchema = z.array(savedMealSchema);
/* ----- POST /me/saved-meals/:id/variants (T30.1) -------------------------- */
/**
 * Save an accepted #ref modification as a new, independently-reusable saved
 * meal. Sources from already-persisted meal_entry rows (the ref+delta log
 * just created them) — same mechanism as `createSavedMealSchema.fromEntryIds`,
 * just parented to the saved meal the log referenced (the `:id` path param).
 */
export const createSavedMealVariantSchema = z.object({
    name: z.string().min(1).max(120),
    fromEntryIds: z.array(z.string().uuid()).min(1),
});
/* ----- GET /me/saved-meals/autocomplete (T30.1 — composer #ref) ----------- */
/** Query for the composer's `#` autocomplete overlay. Empty query returns the
 *  user's most-recently-used saved meals (same default as the plain list). */
export const savedMealAutocompleteQuerySchema = z.object({
    query: z.string().max(120).default(''),
});
const autocompleteMacroSummarySchema = z.object({
    kcal: z.number().nonnegative(),
    proteinG: z.number().nonnegative(),
    carbsG: z.number().nonnegative(),
    fatG: z.number().nonnegative(),
});
/** A saved meal match — carries the `savedMealId` a #ref submission needs. */
export const savedMealAutocompleteMatchSchema = z.object({
    kind: z.literal('savedMeal'),
    savedMealId: z.string().uuid(),
    name: z.string(),
    macros: autocompleteMacroSummarySchema,
});
/**
 * A match from a recent (unsaved) day's log — same food name logged within
 * the last 14 days without ever being bundled into a saved meal ("also
 * matches recent days", design §2). Has no savedMealId yet: the client
 * materializes one via `POST /me/saved-meals` with `fromEntryIds` before
 * submitting it as a #ref.
 */
export const recentDayAutocompleteMatchSchema = z.object({
    kind: z.literal('recentDay'),
    name: z.string(),
    date: z.string().date(),
    fromEntryIds: z.array(z.string().uuid()),
    macros: autocompleteMacroSummarySchema,
});
export const savedMealAutocompleteItemSchema = z.discriminatedUnion('kind', [
    savedMealAutocompleteMatchSchema,
    recentDayAutocompleteMatchSchema,
]);
export const savedMealAutocompleteResponseSchema = z.object({
    items: z.array(savedMealAutocompleteItemSchema),
});
//# sourceMappingURL=saved-meal.js.map